import {
  JOURNAL_SECTIONS,
  type JournalAction,
  type JournalActor,
  type JournalChange,
  type JournalOp,
  type JournalSection,
  journalFieldTitle,
} from '@epc/contracts';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { show, valueTitle } from '../../../components/History';
import { api } from '../../../lib/api';
import { plural } from '../../../lib/format';
import { Badge } from '../../../ui/feedback';

/* Журнал изменений (ДС-11): общие подписи, строки «было → стало», отбор и списки с подгрузкой */

export const OP_WORD: Record<JournalOp, string> = { I: 'Создание', U: 'Изменение', D: 'Удаление' };

/** Что отмена сделает с записью: строки пробного прогона — это изменения самой отмены */
export const EFFECT_WORD: Record<JournalOp, string> = { I: 'Восстановится', U: 'Изменится', D: 'Удалится' };

/** Кто и что видит в журнале, может ли отменять */
export interface JournalMeta {
  actors: Array<{ id: string; fullName: string }>;
  projects: Array<{ id: string; title: string }>;
  canRevert: boolean;
}

/** Название поля: от сервера (с подписью связанной записи) или по словарю журнала */
export function fieldTitle(field: string, title?: string): string {
  return title ?? journalFieldTitle(field);
}

export function sectionTitle(s: JournalSection): string {
  return JOURNAL_SECTIONS[s];
}

export function actorName(a: JournalActor): string {
  return a?.fullName ?? 'Портал';
}

/** Значение поля словами: подпись связанной записи, статус словом, дата, «да/нет» */
export function valueText(table: string, c: JournalChange, side: 'before' | 'after'): string {
  if (c.hidden) return 'скрыто';
  const label = side === 'before' ? c.beforeLabel : c.afterLabel;
  if (label) return label;
  return show(valueTitle(table, c.field, side === 'before' ? c.before : c.after), c.field);
}

const HIDDEN_HINT = 'Нет доступа к суммам этого раздела или это личная запись';

/** Поля изменения: было → стало; у созданной записи — значения, у удалённой — что было */
export function ChangeRows({
  op,
  table,
  changes,
  limit = 8,
}: {
  op: JournalOp;
  table: string;
  changes: JournalChange[];
  limit?: number;
}) {
  const [all, setAll] = useState(false);
  if (!changes.length) return null;
  const shown = all ? changes : changes.slice(0, limit);
  return (
    <dl className="mt-2 flex flex-col gap-1.5 rounded-md bg-surface-2 px-3 py-2.5 text-[13px]">
      {shown.map((c) => (
        <div key={c.field} className="grid grid-cols-[minmax(92px,34%)_1fr] gap-2">
          <dt className="[overflow-wrap:anywhere] text-ink-2">{fieldTitle(c.field, c.title)}</dt>
          <dd className="min-w-0 [overflow-wrap:anywhere] text-ink">
            {c.hidden ? (
              <span className="text-ink-3 italic" title={HIDDEN_HINT}>
                скрыто
              </span>
            ) : op === 'U' ? (
              <>
                <span className="sr-only">было: </span>
                <span className="text-ink-2 line-through decoration-ink-3">
                  {valueText(table, c, 'before')}
                </span>
                <span className="px-1.5 text-ink-3" aria-hidden>
                  →
                </span>
                <span className="sr-only">стало: </span>
                <span>{valueText(table, c, 'after')}</span>
              </>
            ) : (
              valueText(table, c, op === 'I' ? 'after' : 'before')
            )}
          </dd>
        </div>
      ))}
      {changes.length > limit ? (
        <div>
          <button
            type="button"
            onClick={() => setAll((v) => !v)}
            className="inline-flex min-h-11 items-center text-[13px] font-medium text-ink underline underline-offset-2 md:min-h-0"
          >
            {all ? 'Свернуть' : `Ещё ${plural(changes.length - limit, 'поле', 'поля', 'полей')}`}
          </button>
        </div>
      ) : null}
    </dl>
  );
}

/** Сколько записей отправка создала, изменила, удалила */
export function CountBadges({ counts }: { counts: JournalAction['counts'] }) {
  return (
    <>
      {counts.created ? <Badge tone="success">создано: {counts.created}</Badge> : null}
      {counts.updated ? <Badge>изменено: {counts.updated}</Badge> : null}
      {counts.deleted ? <Badge tone="danger">удалено: {counts.deleted}</Badge> : null}
    </>
  );
}

/** Отменена ли отправка и не отмена ли она сама */
export function ActionStateBadge({ a }: { a: Pick<JournalAction, 'state' | 'revertOf'> }) {
  if (a.revertOf)
    return a.revertOf.undone ? <Badge>отмена отменена</Badge> : <Badge tone="info">отмена</Badge>;
  if (a.state === 'reverted') return <Badge tone="warning">отменено</Badge>;
  if (a.state === 'partly') return <Badge tone="warning">отменено частично</Badge>;
  return null;
}

/** Адрес действия без служебной приставки: «contracts/:id/approve» */
export function routeText(route: string | null): string | null {
  return route ? route.replace(/^\/api\/v1\//, '') : null;
}

export function methodWord(method: string | null): string {
  if (method === 'DELETE') return 'Удаление';
  if (method === 'PATCH' || method === 'PUT') return 'Изменение';
  return 'Отправка';
}

/** Почему отправка ничего не сохранила — по ответу портала */
export function attemptReason(status: number | null): string {
  if (status === null) return 'Ответ не записан';
  if (status === 401) return 'Нужен вход';
  if (status === 403) return 'Нет права — ничего не изменено';
  if (status === 404) return 'Запись не найдена';
  if (status === 409) return 'Не прошла проверку или запись уже изменили';
  if (status === 413) return 'Слишком большой файл';
  if (status === 429) return 'Слишком часто — запрос отклонён';
  if (status >= 500) return 'Сбой портала — ничего не сохранено';
  return 'Ошибка в данных — ничего не сохранено';
}

/* ───────────────────────── Отбор ───────────────────────── */

export interface JournalFilters {
  q: string;
  actorId: string;
  section: string;
  projectId: string;
  op: string;
  from: string;
  to: string;
  system: boolean;
}

export const NO_FILTERS: JournalFilters = {
  q: '',
  actorId: '',
  section: '',
  projectId: '',
  op: '',
  from: '',
  to: '',
  system: false,
};

export type JournalQueryParams = Record<string, string | undefined>;

/** Отбор → параметры запроса: даты — по часам человека, с начала первого дня до конца последнего */
export function filtersQuery(f: JournalFilters): JournalQueryParams {
  const q = f.q.trim();
  return {
    q: q.length >= 2 ? q : undefined,
    actorId: f.actorId || undefined,
    section: f.section || undefined,
    projectId: f.projectId || undefined,
    op: f.op || undefined,
    from: f.from ? new Date(`${f.from}T00:00:00`).toISOString() : undefined,
    to: f.to ? new Date(`${f.to}T23:59:59.999`).toISOString() : undefined,
    system: f.system ? 'true' : undefined,
  };
}

export function activeFilterCount(f: JournalFilters): number {
  return [f.q.trim().length >= 2, f.actorId, f.section, f.projectId, f.op, f.from, f.to, f.system].filter(
    Boolean,
  ).length;
}

/** Список журнала с подгрузкой «Показать ещё»: точный курсор из ответа или время последней строки */
export function useJournalList<T extends { at: string; cursor?: string }>(
  path: string,
  query: JournalQueryParams,
  limit = 30,
) {
  return useInfiniteQuery({
    queryKey: ['journal', path, query],
    queryFn: ({ pageParam }) => api<T[]>(path, { query: { ...query, ...pageParam, limit } }),
    initialPageParam: {} as { cursor?: string; before?: string },
    getNextPageParam: (last: T[]) => {
      if (last.length < limit) return undefined;
      const tail = last[last.length - 1]!;
      return tail.cursor ? { cursor: tail.cursor } : { before: tail.at };
    },
  });
}
